import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import api from "../lib/api";
import { Mail, Phone, Award, ShieldCheck, LogOut, TrendingUp, ArrowDownCircle, MessageCircle, FileText, ChevronRight } from "lucide-react";

export default function Profile() {
    const { user, logout } = useAuth();
    const navigate = useNavigate();
    const [loy, setLoy] = useState(null);
    const handleLogout = async () => { await logout(); navigate("/login"); };

    useEffect(() => {
        if (user?.role === "player") api.get("/loyalty/me").then(({ data }) => setLoy(data));
    }, [user]);

    if (!user) return null;

    return (
        <div className="max-w-2xl mx-auto px-4 sm:px-6 py-6">
            <div className="bg-slate-900 border border-slate-800 rounded-lg p-6 sm:p-8 text-center">
                <div className="w-20 h-20 mx-auto rounded-full bg-[#00FF66] flex items-center justify-center font-display text-3xl font-bold text-black uppercase">
                    {user.name?.charAt(0)}
                </div>
                <h1 className="font-display text-3xl font-bold uppercase text-white mt-4" data-testid="profile-name">{user.name}</h1>
                <div className="text-xs uppercase tracking-widest text-[#00FF66] mt-1">{user.role.replace("_", " ")}</div>

                <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-3 text-left">
                    <Field icon={Mail} label="Email" value={user.email} />
                    {user.phone && <Field icon={Phone} label="Phone" value={user.phone} />}
                </div>

                {["super_admin", "admin"].includes(user.role) && (
                    <button onClick={() => navigate("/admin")} data-testid="goto-admin-btn"
                        className="mt-6 inline-flex items-center gap-2 px-5 h-10 bg-slate-800 hover:bg-slate-700 border border-slate-700 rounded text-white font-bold uppercase text-xs tracking-widest">
                        <ShieldCheck className="w-4 h-4 text-[#00FF66]" /> Open Admin Dashboard
                    </button>
                )}

                <button onClick={handleLogout} data-testid="profile-logout-btn"
                    className="mt-6 w-full sm:w-auto inline-flex items-center justify-center gap-2 px-6 h-11 bg-slate-950 hover:bg-red-950 border border-red-900 text-red-400 rounded font-bold uppercase tracking-widest text-xs">
                    <LogOut className="w-4 h-4" /> Logout
                </button>
            </div>

            {/* Loyalty section — players only */}
            {user.role === "player" && loy && (
                <div className="mt-6 bg-slate-900 border border-slate-800 rounded-lg p-6" data-testid="profile-loyalty">
                    <div className="flex items-center gap-2 mb-1">
                        <Award className="w-5 h-5 text-[#00FF66]" />
                        <h2 className="font-display text-xl font-bold uppercase text-white">My Loyalty</h2>
                    </div>
                    <p className="text-[11px] text-slate-500 mb-4">100 pts after each completed booking · Redeem 20% of balance once you cross 500 pts</p>

                    <div className="grid grid-cols-3 gap-3">
                        <Stat label="Balance" value={loy.balance} highlight testid="loy-balance" />
                        <Stat label="Lifetime Earned" value={loy.lifetime_earned} icon={TrendingUp} testid="loy-earned" />
                        <Stat label="Lifetime Redeemed" value={loy.lifetime_redeemed} icon={ArrowDownCircle} testid="loy-redeemed" />
                    </div>

                    {loy.balance < 500 && (
                        <div className="mt-4 text-[11px] uppercase tracking-widest text-amber-400 bg-amber-500/10 border border-amber-500/30 rounded px-3 py-2">
                            {500 - loy.balance} pts away from your first redemption
                        </div>
                    )}

                    {loy.history.length > 0 && (
                        <div className="mt-5">
                            <div className="text-[10px] font-bold tracking-[0.2em] uppercase text-slate-500 mb-2">Recent activity</div>
                            <div className="space-y-2 max-h-64 overflow-y-auto">
                                {loy.history.map(h => (
                                    <div key={h.booking_id} data-testid={`loy-row-${h.booking_id}`}
                                        className="flex items-center justify-between text-xs bg-slate-950 border border-slate-800 rounded px-3 py-2">
                                        <div>
                                            <div className="font-bold text-white">{h.booking_code}</div>
                                            <div className="text-slate-500 text-[10px] uppercase tracking-widest">{h.booking_date}</div>
                                        </div>
                                        <div className="flex items-center gap-3">
                                            {h.earned > 0 && <span className="text-[#00FF66] font-bold">+{h.earned}</span>}
                                            {h.redeemed > 0 && <span className="text-amber-400 font-bold">−{h.redeemed}</span>}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                </div>
            )}

            <div className="mt-8 text-center text-xs text-slate-500">
                Drive or Kick — Multi-sport turf platform
            </div>

            {/* Profile menu — Contact Us & Cancellation Policy (Mod 4) */}
            <div className="mt-6 bg-slate-900 border border-slate-800 rounded-lg overflow-hidden" data-testid="profile-menu">
                <MenuRow icon={MessageCircle} label="Contact Us" to="/contact" testid="menu-contact" />
                <MenuRow icon={FileText} label="Cancellation & Refund Policy" to="/cancellation-policy" testid="menu-cancellation" />
            </div>

            {/* Footer links — About / Terms / Privacy */}
            <div className="mt-6 text-center text-[11px] text-slate-500" data-testid="profile-footer">
                <Link to="/about" data-testid="footer-about" className="text-slate-400 hover:text-[#00FF66] underline-offset-2 hover:underline">About Us</Link>
                <span className="mx-2 text-slate-700">·</span>
                <Link to="/terms" data-testid="footer-terms" className="text-slate-400 hover:text-[#00FF66] underline-offset-2 hover:underline">Terms of Use</Link>
                <span className="mx-2 text-slate-700">·</span>
                <Link to="/privacy" data-testid="footer-privacy" className="text-slate-400 hover:text-[#00FF66] underline-offset-2 hover:underline">Privacy Policy</Link>
                <div className="mt-2 text-[10px] text-slate-600 uppercase tracking-widest">© Drive or Kick · New Delhi</div>
                <div className="mt-3 pt-3 border-t border-slate-800 text-[10px] text-slate-500 leading-relaxed" data-testid="brand-footer">
                    Drive or Kick is a brand operated by ALL SPORTS.<br />
                    © 2026 ALL SPORTS. All Rights Reserved.
                </div>
            </div>
        </div>
    );
}

function MenuRow({ icon: Icon, label, to, testid }) {
    return (
        <Link to={to} data-testid={testid}
            className="flex items-center justify-between gap-3 px-4 py-4 border-b border-slate-800 last:border-b-0 hover:bg-slate-800/30 transition-colors">
            <div className="flex items-center gap-3">
                <div className="w-9 h-9 flex items-center justify-center rounded bg-slate-950 border border-slate-800">
                    <Icon className="w-4 h-4 text-[#00FF66]" />
                </div>
                <div className="text-sm font-semibold text-white">{label}</div>
            </div>
            <ChevronRight className="w-4 h-4 text-slate-500" />
        </Link>
    );
}

function Field({ icon: Icon, label, value }) {
    return (
        <div className="flex items-center gap-3 bg-slate-950 border border-slate-800 rounded p-3">
            <div className="w-9 h-9 flex items-center justify-center rounded bg-slate-900 border border-slate-800">
                <Icon className="w-4 h-4 text-[#00FF66]" />
            </div>
            <div>
                <div className="text-[10px] font-bold tracking-[0.2em] uppercase text-slate-400">{label}</div>
                <div className="text-sm text-white">{value}</div>
            </div>
        </div>
    );
}

function Stat({ label, value, icon: Icon, highlight, testid }) {
    return (
        <div className={`rounded-lg border p-3 text-center ${highlight ? "bg-[#00FF66]/10 border-[#00FF66]/40" : "bg-slate-950 border-slate-800"}`} data-testid={testid}>
            {Icon && <Icon className={`w-4 h-4 mx-auto ${highlight ? "text-[#00FF66]" : "text-slate-500"}`} />}
            <div className={`font-display text-2xl font-bold mt-1 ${highlight ? "text-[#00FF66]" : "text-white"}`}>{(value || 0).toLocaleString("en-IN")}</div>
            <div className="text-[10px] font-bold tracking-[0.2em] uppercase text-slate-500 mt-0.5">{label}</div>
        </div>
    );
}
